import React, { SyntheticEvent, useState, useRef } from "react";
import {
  Typography,
  Grid,
  TextField,
  Button,
  ListItemText,
  ListItem,
  List,
  IconButton,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import * as driverActivitiesActions from "../../../ducks/app/driver-activities";
import { useAppDispatch } from "../../../redux-store";
import Dialog from "@mui/material/Dialog";
import { useSelector } from "react-redux";
import { selectStopInvoiceRefsFromActivities } from "../../../ducks/data/route-with-stops/selectors";
import { DateTime } from "luxon";
import { StopNew } from "../../../ducks/data/route-with-stops/types";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import useFeature from "../../../hooks/useFeature";
import { useParams } from "../hooks";
import LoadingButton from "../../../components/LoadingButton";
import { LoadingButton as MuiLoadingButton } from "@mui/lab";
import { parseTrailerIdParam } from "../../../routes";

const AddRefModal = (props: { stop: StopNew; onClose: () => void }) => {
  const { stop, onClose } = props;
  const [ref, setRef] = useState("");
  const [loading, setLoading] = useState(false);
  const [refIdForEdit, setRefIdForEdit] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const refs = useSelector(selectStopInvoiceRefsFromActivities(stop.id));
  const { t } = useTranslation(["stopPage", "components"]);
  const inputRef = useRef<HTMLInputElement>(null);
  const params = useParams();
  const trailerId = (params.truckId as string) || "";
  const routeId = (params.routeId as string) || "";
  const unpoweredTrailerId = parseTrailerIdParam(params.trailerId as string);

  const onSaveClick = async () => {
    setLoading(true);
    try {
      await dispatch(
        driverActivitiesActions.editInvoiceReference({
          routeId,
          id: refIdForEdit!,
          ref,
          cargoId: stop.cargoId,
          stopId: stop.id,
          truckId: trailerId,
          unpoweredTrailerId,
        })
      ).unwrap();
      setRefIdForEdit(null);
      setRef("");
      onClose();
    } catch {
      // Already reported and shown by the thunk; keep the modal open.
    } finally {
      setLoading(false);
    }
  };

  const onEditClick = async (refId: string) => {
    const r = refs.find((r) => r.id === refId);
    if (r) {
      setRefIdForEdit(refId);
      setRef(r.ref);
      inputRef.current?.focus();
    }
  };

  const onSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    if (refIdForEdit) {
      onSaveClick();
    } else {
      onAddClick();
    }
  };

  const onAddClick = async () => {
    setLoading(true);
    try {
      await dispatch(
        driverActivitiesActions.addInvoiceReference({
          routeId,
          cargoId: stop.cargoId,
          stopId: stop.id,
          ref,
          truckId: trailerId,
          unpoweredTrailerId,
        })
      ).unwrap();
      onClose();
    } catch {
      // Already reported and shown by the thunk; keep the modal open.
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <div className="px-8 py-4">
        <Typography variant="h6" component="h2" pt={2}>
          {t("stopPage:ref.heading")}
        </Typography>
        <List>
          {refs.map((r) => (
            <RefListItem
              key={r.id}
              r={r}
              routeId={routeId}
              cargoId={stop.cargoId}
              stopId={stop.id}
              trailerId={trailerId}
              unpoweredTrailerId={unpoweredTrailerId}
              onEditClick={onEditClick}
            />
          ))}
        </List>
        <form onSubmit={onSubmit} className="pt-4">
          <Grid container spacing={2} direction="column">
            <Grid columns={12}>
              <TextField
                autoFocus
                fullWidth
                type="text"
                inputRef={inputRef}
                value={ref}
                label={t("stopPage:ref.textFieldLabel")}
                placeholder="ABC123"
                onChange={(e) => {
                  setRef(e.target.value);
                }}
              />
            </Grid>
            <Grid columns={12}>
              <div className="flex gap-2 justify-between w-100 mt-4">
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  onClick={onClose}
                >
                  {t("stopPage:ref.backButtonLabel")}
                </Button>
                <LoadingButton
                  loading={loading}
                  type="submit"
                  size="large"
                  fullWidth
                  variant="contained"
                >
                  <span>
                    {refIdForEdit
                      ? t("components:buttonLabels.save")
                      : t("stopPage:ref.addButtonLabel")}
                  </span>
                </LoadingButton>
              </div>
            </Grid>
          </Grid>
        </form>
      </div>
    </Dialog>
  );
};

const RefListItem = (props: {
  r: { id: string; createdAt: string; ref: string };
  cargoId: string;
  stopId: string;
  routeId: string;
  trailerId: string;
  unpoweredTrailerId: string | null;
  onEditClick: (refId: string) => void;
}) => {
  const { r, cargoId, stopId, routeId, trailerId, unpoweredTrailerId, onEditClick } =
    props;
  const { t } = useTranslation(["stopPage", "components"]);
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const dispatch = useAppDispatch();
  const isEditDeleteEnabled = useFeature("driver-app-edit-delete-invoice-refs");

  const onConfirmDeleteClick = async () => {
    setRequestInProgress(true);
    dispatch(
      driverActivitiesActions.deleteInvoiceReference({
        id: r.id,
        cargoId,
        routeId,
        stopId,
        truckId: trailerId,
        unpoweredTrailerId,
      })
    )
      .unwrap()
      .then(() => {
        setShowConfirmDeleteDialog(false);
      })
      .catch(() => {
        // Already reported and shown by the thunk.
      })
      .finally(() => setRequestInProgress(false));
  };

  return (
    <>
      <Dialog
        open={showConfirmDeleteDialog}
        onClose={() => setShowConfirmDeleteDialog(false)}
      >
        <DialogTitle>
          <Typography variant="h6" component="h2" pt={2}>
            {t("stopPage:ref.deleteConfirmTitle")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" mt={2}>
            {t("stopPage:ref.deleteConfirmMessage")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <div className="flex gap-2 justify-between w-100 mt-4">
            <Button
              variant="outlined"
              size="large"
              fullWidth
              disabled={requestInProgress}
              onClick={() => setShowConfirmDeleteDialog(false)}
            >
              {t("components:buttonLabels.cancel")}
            </Button>
            <MuiLoadingButton
              type="button"
              fullWidth
              size="large"
              variant="contained"
              color="error"
              disabled={requestInProgress}
              onClick={onConfirmDeleteClick}
            >
              {t("components:buttonLabels.delete")}
            </MuiLoadingButton>
          </div>
        </DialogActions>
      </Dialog>
      <ListItem sx={{ pl: 0 }} key={r.createdAt} disableGutters divider>
        <div className="flex flex-col justify-between w-full">
          <div className="flex justify-between flex-grow items-center">
            <div style={{ color: "var(--gray-500)", fontSize: "0.9rem" }}>
              {DateTime.fromISO(r.createdAt).toLocaleString(
                DateTime.DATETIME_SHORT
              )}
            </div>
            {isEditDeleteEnabled && (
              <div className="flex items-center">
                <IconButton onClick={() => onEditClick(r.id)}>
                  <EditIcon style={{ color: "var(--gray-500)" }} />
                </IconButton>
                <IconButton
                  style={{ paddingRight: 0 }}
                  onClick={() => setShowConfirmDeleteDialog(true)}
                >
                  <DeleteOutlinedIcon style={{ color: "red" }} />
                </IconButton>
              </div>
            )}
          </div>
          <ListItemText primary={r.ref} />
        </div>
      </ListItem>
    </>
  );
};

export default AddRefModal;
